import * as React from "react";
import { useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { useFormikContext } from "formik";

import {
  Checkbox,
  FormikSelectField,
  FormikTextInputField,
  Panel,
  Typography,
  VStack,
} from "@smartrent/ui";

import { GooglePlacesAutocomplete } from "@/common/google-places-autocomplete";

import {
  toShippingMethodSelectOptions,
  type ShippingMethodOption,
} from "../shipping-methods";
import type { NetsuiteFormValues as FormValues } from "../types";

interface NetsuiteShippingFormProps {
  shippingMethods: readonly ShippingMethodOption[];
}

export const NetsuiteShippingForm: React.FC<NetsuiteShippingFormProps> = ({
  shippingMethods,
}) => {
  const { values: formValues, setFieldValue } = useFormikContext<FormValues>();
  const netsuiteData = formValues.netsuiteData;
  const useCustomAddress = Boolean(formValues.is_custom_shipping_address);

  const savedCustomAddressRef = useRef<{
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
  } | null>(null);

  const setUseCustomAddressValue = (value: boolean | "mixed") => {
    const isCustomAddress = Boolean(value);
    setFieldValue("is_custom_shipping_address", isCustomAddress);
    if (isCustomAddress) {
      if (savedCustomAddressRef.current) {
        setFieldValue(
          "shipping_address_line1",
          savedCustomAddressRef.current.line1
        );
        setFieldValue(
          "shipping_address_line2",
          savedCustomAddressRef.current.line2
        );
        setFieldValue(
          "shipping_address_city",
          savedCustomAddressRef.current.city
        );
        setFieldValue(
          "shipping_address_state",
          savedCustomAddressRef.current.state
        );
        setFieldValue(
          "shipping_address_zip",
          savedCustomAddressRef.current.zip
        );
      } else {
        setFieldValue("shipping_address_line1", "");
        setFieldValue("shipping_address_line2", "");
        setFieldValue("shipping_address_city", "");
        setFieldValue("shipping_address_state", "");
        setFieldValue("shipping_address_zip", "");
      }
    } else if (netsuiteData) {
      savedCustomAddressRef.current = {
        line1: formValues.shipping_address_line1,
        line2: formValues.shipping_address_line2 || "",
        city: formValues.shipping_address_city,
        state: formValues.shipping_address_state,
        zip: formValues.shipping_address_zip,
      };
      setFieldValue(
        "shipping_address_line1",
        netsuiteData.shippingAddress.address1
      );
      setFieldValue(
        "shipping_address_line2",
        netsuiteData.shippingAddress.address2 || ""
      );
      setFieldValue("shipping_address_city", netsuiteData.shippingAddress.city);
      setFieldValue(
        "shipping_address_state",
        netsuiteData.shippingAddress.state
      );
      setFieldValue("shipping_address_zip", netsuiteData.shippingAddress.zip);
    } else {
      setFieldValue("shipping_address_line1", "");
      setFieldValue("shipping_address_line2", "");
      setFieldValue("shipping_address_city", "");
      setFieldValue("shipping_address_state", "");
      setFieldValue("shipping_address_zip", "");
    }
  };

  // Auto-populate shipping address from NetSuite data
  useEffect(() => {
    if (netsuiteData && !useCustomAddress) {
      setFieldValue("shipping_address_company", netsuiteData.customerName);
      setFieldValue("shipping_address_property", netsuiteData.propertyName);
      setFieldValue(
        "shipping_address_line1",
        netsuiteData.shippingAddress.address1
      );
      setFieldValue(
        "shipping_address_line2",
        netsuiteData.shippingAddress.address2 || ""
      );
      setFieldValue("shipping_address_city", netsuiteData.shippingAddress.city);
      setFieldValue(
        "shipping_address_state",
        netsuiteData.shippingAddress.state
      );
      setFieldValue("shipping_address_zip", netsuiteData.shippingAddress.zip);
    }
  }, [netsuiteData, useCustomAddress, setFieldValue]);

  return (
    <Panel>
      <View style={styles.sectionHeader}>
        <Typography variation="bold.title.three">Shipping Address</Typography>
        <Typography variation="regular.caption.medium">
          {useCustomAddress
            ? "Enter a custom shipping address"
            : "Address is pre-filled from property information"}
        </Typography>
      </View>

      <VStack spacing={16}>
        <FormikSelectField
          name="shipping_method_slug"
          label="Shipping Method"
          options={toShippingMethodSelectOptions(shippingMethods)}
          required
        />

        <FormikTextInputField
          name="shipping_address_company"
          label="Company"
          disabled
          textInputProps={{
            placeholder: "Auto-filled from property",
          }}
        />

        <FormikTextInputField
          name="shipping_address_property"
          label="Property"
          disabled
          textInputProps={{
            placeholder: "Auto-filled from property",
          }}
        />

        <Checkbox
          name="is_custom_shipping_address"
          checked={useCustomAddress}
          onChangeChecked={setUseCustomAddressValue}
          label="Use custom shipping address"
          testID="hardware_order_checkbox_custom_shipping"
          disabled={!netsuiteData}
          style={styles.checkbox}
        />

        {useCustomAddress ? (
          <GooglePlacesAutocomplete
            name="shipping_address_line1"
            label="Custom Shipping Address Line 1"
            cityFieldName="shipping_address_city"
            stateFieldName="shipping_address_state"
            zipFieldName="shipping_address_zip"
            testID="hardware_order_input_custom_shipping_address"
          />
        ) : (
          <FormikTextInputField
            name="shipping_address_line1"
            label="Address Line 1"
            disabled
            textInputProps={{
              placeholder: "Auto-filled from property",
            }}
          />
        )}

        <FormikTextInputField
          name="shipping_address_line2"
          label={
            useCustomAddress
              ? "Custom Shipping Address Line 2"
              : "Address Line 2"
          }
          disabled={!useCustomAddress}
          textInputProps={{
            placeholder: useCustomAddress ? "" : "Optional",
          }}
        />

        <FormikTextInputField
          name="shipping_address_city"
          label="City"
          disabled={!useCustomAddress}
          textInputProps={{
            placeholder: useCustomAddress ? "" : "Auto-filled from property",
          }}
        />

        <FormikTextInputField
          name="shipping_address_state"
          label="State"
          disabled={!useCustomAddress}
          textInputProps={{
            placeholder: useCustomAddress ? "" : "Auto-filled from property",
          }}
        />

        <FormikTextInputField
          name="shipping_address_zip"
          label="Zip Code"
          disabled={!useCustomAddress}
          textInputProps={{
            placeholder: useCustomAddress ? "" : "Auto-filled from property",
          }}
        />

        <View style={styles.divider} />

        <Typography type="title4">Shipping Contact</Typography>

        <FormikTextInputField
          name="shipping_address_contact_name"
          label="Contact Name"
          textInputProps={{
            placeholder: "Your name",
          }}
        />

        <FormikTextInputField
          name="shipping_address_contact_phone"
          label="Contact Phone"
          textInputProps={{
            placeholder: "(555) 123-4567",
          }}
        />

        <FormikTextInputField
          name="shipping_address_contact_email"
          label="Contact Email"
          textInputProps={{
            placeholder: "your.email@example.com",
          }}
        />
      </VStack>
    </Panel>
  );
};

const styles = StyleSheet.create({
  sectionHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  divider: {
    height: 1,
    backgroundColor: "#e0e0e0",
    marginVertical: 8,
  },
  checkbox: {
    alignSelf: "flex-start",
  },
});
